import React, { useEffect, useState, useRef } from "react";
import useMousePosition from "../../hooks/useMousePosition";

const CustomCursor = () => {
    const { x, y } = useMousePosition();
    const [isHovering, setIsHovering] = useState(false);
    const [isMobileDevice, setIsMobileDevice] = useState(false);
    const cursorRef = useRef(null);
    const requestRef = useRef();
    const positionRef = useRef({ x: 0, y: 0 });

    // Vérifier si c'est un appareil mobile au chargement et à chaque redimensionnement
    useEffect(() => {
        const checkIfMobile = () => {
            setIsMobileDevice(window.innerWidth < 768);
        };

        // Vérifier au chargement initial
        checkIfMobile();

        // Ajouter un event listener pour le redimensionnement
        window.addEventListener("resize", checkIfMobile);

        return () => {
            window.removeEventListener("resize", checkIfMobile);
        };
    }, []);

    // Mettre à jour la référence de position quand x ou y changent
    useEffect(() => {
        positionRef.current = { x, y };
    }, [x, y]);

    // Animation du curseur
    useEffect(() => {
        // Ne pas animer si c'est un appareil mobile
        if (isMobileDevice) return;

        const animateCursor = () => {
            if (cursorRef.current) {
                const { x, y } = positionRef.current;
                const cursorSize = isHovering ? 60 : 20; // Augmentation de la taille au hover
                cursorRef.current.style.transform = `translate(${
                    x - cursorSize / 2
                }px, ${y - cursorSize / 2}px)`;
            }
            requestRef.current = requestAnimationFrame(animateCursor);
        };

        requestRef.current = requestAnimationFrame(animateCursor);

        return () => {
            cancelAnimationFrame(requestRef.current);
        };
    }, [isHovering, isMobileDevice]);

    // Ajouter un effet pour masquer le curseur par défaut
    useEffect(() => {
        // Ne pas modifier le curseur sur mobile
        if (isMobileDevice) return;

        // Masquer le curseur par défaut sur le body
        document.body.style.cursor = "none";

        // Ajouter une règle CSS pour masquer tous les curseurs sur les éléments cliquables
        const styleElement = document.createElement("style");
        styleElement.innerHTML = `
      @media (min-width: 768px) {
        a, button, .clickable, [role="button"] {
          cursor: none !important;
        }
      }
      @keyframes fadeIn {
        from { opacity: 0; transform: scale(0.8); }
        to { opacity: 1; transform: scale(1); }
      }
      .arrow-icon {
        transform: rotate(-45deg);
        animation: fadeIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
      }
    `;
        document.head.appendChild(styleElement);

        return () => {
            document.body.style.cursor = "auto";
            document.head.removeChild(styleElement);
        };
    }, [isMobileDevice]);

    // Détecter quand le curseur est au-dessus d'éléments cliquables
    useEffect(() => {
        // Ne pas détecter les survols sur mobile
        if (isMobileDevice) return;

        // Optimisation: utiliser un debounce léger pour éviter les appels trop fréquents
        let hoverTimeout;

        const handleMouseOver = (e) => {
            clearTimeout(hoverTimeout);

            const target = e.target;
            const isClickable =
                target.tagName.toLowerCase() === "a" ||
                target.tagName.toLowerCase() === "button" ||
                target.closest("a") ||
                target.closest("button") ||
                target.classList.contains("clickable") ||
                (target.hasAttribute("role") &&
                    target.getAttribute("role") === "button");

            hoverTimeout = setTimeout(() => {
                setIsHovering(isClickable);
            }, 10);
        };

        document.addEventListener("mouseover", handleMouseOver);

        return () => {
            document.removeEventListener("mouseover", handleMouseOver);
            clearTimeout(hoverTimeout);
        };
    }, [isMobileDevice]);

    // Sur mobile, ne pas rendre le composant
    if (isMobileDevice) {
        return null;
    }

    const cursorSize = isHovering ? 60 : 20; // Augmentation de la taille au hover

    return (
        <div
            ref={cursorRef}
            className="fixed top-0 left-0 pointer-events-none mix-blend-difference flex items-center justify-center"
            style={{
                width: `${cursorSize}px`,
                height: `${cursorSize}px`,
                borderRadius: "50%",
                zIndex: 9999,
                backgroundColor: "white",
                transition:
                    "width 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), height 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)",
            }}
        >
            {isHovering && (
                <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="arrow-icon"
                    style={{
                        color: "black",
                    }}
                >
                    <path
                        d="M5 19L19 5"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    <path
                        d="M19 17V5H7"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            )}
        </div>
    );
};

export default CustomCursor;
